import { fetchFragmentArea } from '@gialaboratory/sdk/report-check-fragments';
import { FragmentRenderer } from '@gialaboratory/sdk/fragment-renderer';
import { useQuery } from '@tanstack/react-query';
import React, { useContext } from 'react';
import AppPromo from '@@components/AppPromo/AppPromo';
import GradingScale from '@@components/GradingScale/GradingScale';
import { TLocale } from '@@config/locale/';
import { TReportTypeCode } from '@@config/report';
import { LocaleContext, TLocaleContext } from '@@contexts/LocaleContext';
import { getMagnoliaRestBaseURL } from '@@utils/utils';

const FRAGMENT_PAGE_NAME = 'right-rail' as const;

const fetchRightRailContent = async (reportTypeCode: TReportTypeCode, locale: TLocale) => {
    const baseUrl = getMagnoliaRestBaseURL();
    try {
        return await fetchFragmentArea(`${FRAGMENT_PAGE_NAME}-${reportTypeCode}`, locale, { baseUrl });
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(
            '[RightRail] Fragment fetch failed:',
            msg,
            'url:',
            `${baseUrl}/.rest/api/report-check/fragments/${FRAGMENT_PAGE_NAME}-${reportTypeCode}/${locale}`
        );
        throw err;
    }
};

type TRightRailProps = {
    reportTypeCode: TReportTypeCode;
    cutGrade: string;
};

const RightRail: React.FC<TRightRailProps> = ({ reportTypeCode, cutGrade = '' }) => {
    const { locale }: TLocaleContext = useContext(LocaleContext);
    const hasCutGrade = cutGrade !== '';

    const {
        data: content,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['reportCheckFragment', FRAGMENT_PAGE_NAME, locale, reportTypeCode],
        queryFn: () => fetchRightRailContent(reportTypeCode, locale),
        staleTime: 15 * 60 * 1000, // 15 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
    });

    const renderSlot = (_nodeId: string, node: Record<string, unknown>) => {
        const id = node['id'] ?? node['@id'];
        if (reportTypeCode === 'DD' && (id === 'xgrading-scale' || String(id).includes('xgrading-scale'))) {
            return <GradingScale hasCutGrade={hasCutGrade} locale={locale} />;
        }
        if (id === 'xapp-promo' || String(id).includes('xapp-promo')) {
            return <AppPromo locale={locale} />;
        }
        return null;
    };

    if (isLoading) {
        return (
            <div className='flex flex-wrap h-full px-0 md:pl-4'>
                <div className='flex-1'>
                    <div className='skeleton w-full h-full'></div>
                </div>
            </div>
        );
    }

    if (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error('[RightRail] Error loading right rail content:', msg);
        return (
            <div className='flex flex-wrap h-full px-0 md:pl-4' role='alert'>
                <div className='flex-1'>
                    <p>
                        Right rail content is not available. Ensure the report-check API is running at{' '}
                        {getMagnoliaRestBaseURL()}.
                    </p>
                </div>
            </div>
        );
    }

    const areaContent = content?.main;

    if (!areaContent) {
        return (
            <div className='flex flex-wrap h-full px-0 md:pl-4'>
                <div className='flex-1'>
                    <div className='skeleton w-full h-full'></div>
                </div>
            </div>
        );
    }

    return (
        <div className='flex flex-wrap h-full px-0 md:pl-4'>
            <div className='flex-1'>
                <FragmentRenderer
                    className='gia-fragment-content'
                    content={areaContent as Record<string, unknown>}
                    locale={locale}
                    magnoliaPublicBaseUrl={getMagnoliaRestBaseURL()}
                    renderSlot={renderSlot}
                />
            </div>
        </div>
    );
};

export default RightRail;
